import { LoadingSpinner } from "@shared/components/LoadingSpinner";
import { hexToRGB, tailwindConfig } from "../../../utils/Utils";
import { useAnalyticsData } from "../hooks/useAnalyticsData";
import RealtimeChart from "./charts/RealtimeChart";
import theme from "@shared/utils/themeColors";

// Function to create gradient
const chartAreaGradient = (ctx, chartArea, stops) => {
  if (!chartArea) return null;
  const gradient = ctx.createLinearGradient(
    0,
    chartArea.bottom,
    0,
    chartArea.top
  );
  stops.forEach((stop) => gradient.addColorStop(stop.stop, stop.color));
  return gradient;
};

function AverageCorrectAnswers() {
  const { loading, error, analyticsData } = useAnalyticsData();

  if (loading) {
    return (
      <div className={`flex flex-col col-span-full sm:col-span-6 ${theme.bg.primary} shadow-sm rounded-xl p-5`}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex flex-col col-span-full sm:col-span-6 ${theme.bg.primary} shadow-sm rounded-xl p-5`}>
        <p className={`text-center ${theme.text.error}`}>Error: {error}</p>
      </div>
    );
  }

  if (
    !analyticsData?.processSummary ||
    analyticsData.processSummary.length === 0
  ) {
    return (
      <div className={`flex flex-col col-span-full sm:col-span-6 ${theme.bg.primary} shadow-sm rounded-xl p-5`}>
        <p className={`text-center ${theme.text.muted}`}>No data available</p>
      </div>
    );
  }

  // Process data for chart
  const processData = analyticsData.processSummary;

  // Convert ISO strings to Date objects
  const dates = processData.map((process) => new Date(process.completedAt));

  // Calculate percentage of correct answers vs total questions
  const percentages = processData.map(
    (process) =>
      (process.averageCorrect /
        (process.averageIncorrect + process.averageCorrect)) *
      100
  );

  const chartData = {
    labels: dates,
    datasets: [
      {
        data: percentages,
        fill: true,
        backgroundColor: function (context) {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          return chartAreaGradient(ctx, chartArea, [
            {
              stop: 0,
              color: `rgba(${hexToRGB(
                tailwindConfig().theme.colors.violet[500]
              )}, 0)`,
            },
            {
              stop: 1,
              color: `rgba(${hexToRGB(
                tailwindConfig().theme.colors.violet[500]
              )}, 0.2)`,
            },
          ]);
        },
        borderColor: tailwindConfig().theme.colors.violet[500],
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: tailwindConfig().theme.colors.violet[500],
        pointHoverBackgroundColor: tailwindConfig().theme.colors.violet[500],
        pointBorderWidth: 0,
        pointHoverBorderWidth: 0,
        clip: 20,
        tension: 0.2,
      },
    ],
  };

  return (
    <div className={`flex flex-col col-span-full sm:col-span-6 ${theme.bg.primary} shadow-sm rounded-xl`}>
      <header className="px-5 py-4 border-b border-gray-100 dark:border-gray-700/60 flex items-center justify-between">
        <h2 className={`font-semibold ${theme.text.primary}`}>
          Average Correct Answers
        </h2>
        <div className={`text-xs ${theme.text.muted}`}>
          {processData.length} tests analyzed
        </div>
      </header>
      <RealtimeChart
        data={chartData}
        width={595}
        height={248}
        processData={processData}
      />
    </div>
  );
}

export default AverageCorrectAnswers;
